// Pure derivations for the `players` page, moved verbatim out of Players.tsx.

import { getImageUrl } from '@/services/api'
import type { Player, Team } from '@/services/api/types'
import { getTeamDisplay } from '@/utils/teamDisplay'
import type { SportPack } from '@/contexts/SportContext'
import type { TenantDivision } from '@/contexts/LeagueContext'
import { divisionRef } from '../../division'
import type { Option } from '../../template/pages'
import { formatHeight } from '../player/derive'
import type { PlayerCard, PlayerGroup, PlayerStatusFilter } from './types'

export const PAGE_SIZE = 48
export const SEARCH_DEBOUNCE_MS = 300

export function playerCard(p: Player): PlayerCard {
  return {
    id: p.id,
    href: `/players/${p.id}`,
    firstName: p.first_name,
    lastName: p.last_name,
    name: `${p.first_name} ${p.last_name}`,
    nickname: p.nickname || null,
    number: p.jersey_number ?? null,
    position: p.position || null,
    photoUrl: getImageUrl(p.photo_url),
    team: p.team_name
      ? {
          id: p.team_id,
          name: getTeamDisplay({ name: p.team_name, short_code: p.team_short_code }, 'full'),
          short: getTeamDisplay({ name: p.team_name, short_code: p.team_short_code }, 'short'),
          code: p.team_short_code || null,
          logoUrl: getImageUrl(p.team_logo_url),
          color: p.team_primary_color ?? null,
        }
      : null,
    height: formatHeight(p.height_inches),
    raw: p,
  }
}

export function teamOptions(teams: Team[]): Option<number | null>[] {
  return teams.map((t) => ({ value: t.id, label: getTeamDisplay(t, 'full') }))
}

/** Sport-aware position codes (basketball PG/SG/SF/PF/C; soccer GK/DF/MF/FW). Label = code, as today. */
export function positionOptions(sport: SportPack): Option<string | null>[] {
  return sport.positions.map((p) => ({ value: p.code, label: p.code }))
}

export function hasPlayerFilters(search: string, team: string, position: string, status: PlayerStatusFilter): boolean {
  return Boolean(search || team || position || status !== 'active')
}

function byTeam(items: PlayerCard[]): PlayerGroup['byTeam'] {
  const out: PlayerGroup['byTeam'] = []
  const none: PlayerCard[] = []
  for (const p of items) {
    if (!p.team) { none.push(p); continue }
    const hit = out.find((g) => g.team?.id === p.team!.id)
    if (hit) hit.items.push(p)
    else out.push({ team: p.team, items: [p] })
  }
  return none.length ? [...out, { team: null, items: none }] : out
}

/**
 * #583: the loaded players grouped under the live divisions in admin order by
 * `division_ids` (`/public/players?division=all`), then by team. A player in
 * two divisions is in both; players in none come last (division null).
 * #722: `totals` are each division's real counts (not just the loaded page's);
 * a division with players only on later pages still gets its (empty) group.
 */
export function groupPlayersByDivision(
  cards: PlayerCard[],
  live: readonly TenantDivision[],
  totals: Readonly<Record<number, number>> = {},
): PlayerGroup[] {
  const groups = live.map((d) => ({ division: divisionRef(live, d.id), total: (totals[d.id] ?? null) as number | null, items: [] as PlayerCard[] }))
  const none: PlayerCard[] = []
  for (const c of cards) {
    const ids = c.raw.division_ids ?? []
    let placed = false
    groups.forEach((g, i) => { if (ids.includes(live[i].id)) { g.items.push(c); placed = true } })
    if (!placed) none.push(c)
  }
  const out = groups.filter((g) => g.items.length > 0 || (g.total ?? 0) > 0)
  if (none.length) out.push({ division: null, total: null, items: none })
  return out.map((g) => ({ ...g, byTeam: byTeam(g.items) }))
}
